import { Add, Cancel, Delete, Done, Edit, Undo } from "@mui/icons-material";
import { Switch, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Toolbar, Tooltip, Typography } from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import BusAdministrationApiService from "../../adapters/xhr/BusAdministrationApiService";
import BusAutomaticApprovalApiService from "../../adapters/xhr/BusAutomaticApprovalApiService";
import BusBasePriceApiService from "../../adapters/xhr/BusBasePriceApiService";
import authService from "../../components/api-authorization/AuthorizeService";
import { MyDatePicker } from "../../components/my-date-picker/MyDatePicker";
import { MyDateTimePicker } from "../../components/my-date-time-picker/MyDateTimePicker";
import { MyDropdown } from "../../components/my-dropdown/MyDropdown";
import { MyNumberField } from "../../components/my-number-field/MyNumberField";
import { MySelect } from "../../components/my-select/MySelect";
import { MyTextField } from "../../components/my-text-field/MyTextField";
import { AllowedFileExtensions, AutomaticApprovalSettings, DataType, UserRoles } from "../../Constant";
import MyContext from "../../contexts/MyContext";
import SnackbarUtils from "../../SnackbarUtils";
import { formatDate, formatTime, getTrainStationOptions } from "../../Utils";

export const BusSettingsPage = () => {
  const { t } = useTranslation();
  const readOnly = authService._user.profile.role !== UserRoles.SuperAdmin && authService._user.profile.role !== UserRoles.SystemAdmin;
  const [automaticApprovalSettings, setAutomaticApprovalSettings] = useState(null);
  const [automaticApprovalSettingsPrevious, setAutomaticApprovalSettingsPrevious] = useState([]);
  const [basePriceRows, setBasePriceRows] = useState(null);
  const [isAddBasePriceMode, setIsAddBasePriceMode] = useState(false);
  const [autoApproval, setAutoApproval] = useState(true);
  const [basePricePrevious, setBasePricePrevious] = useState([]);
  const myContext = useContext(MyContext);

  const basePriceColumns = [
    { id: "Year", label: t("year"), minWidth: 400, width: 400, editable: false },
    { id: "Value", label: t("value"), minWidth: 400, width: 400, editable: true },
    //
    ...(!readOnly ? [{ id: "Actions", label: t("actions"), minWidth: 170 }] : []),
  ];

  const autoApprovalColumns = [
    { id: "Title", label: t("title"), minWidth: 400, width: 400, editable: false },
    { id: "Value", label: t("value"), minWidth: 400, width: 400, editable: true },
    //
    ...(!readOnly ? [{ id: "Actions", label: t("actions"), minWidth: 170 }] : []),
  ];

  const busesToRemoveColumns = [
    { id: "Value", label: t("bus_number"), minWidth: 400, width: 400, editable: true, type: "number" },
    { id: "FromDate", label: t("from_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    { id: "ToDate", label: t("to_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    //
    ...(!readOnly ? [{ id: "Actions", label: t("actions"), minWidth: 100 }] : []),
  ];

  const getStationName = (code) => {
    return myContext.trainStations?.find((item) => item.code === code)?.name;
  };

  const stationsToRemoveColumns = [
    { id: "Value", label: t("station"), minWidth: 400, width: 400, editable: true, format: getStationName },
    { id: "FromDate", label: t("from_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    { id: "ToDate", label: t("to_date"), minWidth: 400, width: 400, editable: true, type: "date", format: formatDate },
    //
    ...(!readOnly ? [{ id: "Actions", label: t("actions"), minWidth: 100 }] : []),
  ];

  const formatDateTime = (value) => {
    return formatDate(value) + " " + formatTime(value);
  };

  const periodsColumns = [
    {
      id: "FromDate",
      label: t("from_date"),
      minWidth: 400,
      width: 400,
      editable: true,
      type: "date",
      format: (value) => formatDateTime(value),
    },
    {
      id: "ToDate",
      label: t("to_date"),
      minWidth: 400,
      width: 400,
      editable: true,
      type: "date",
      format: (value) => formatDateTime(value),
    },
    ...(!readOnly ? [{ id: "Actions", label: t("actions"), minWidth: 100 }] : []),
  ];

  const fetchStations = useCallback(async () => {
    let response = await BusAdministrationApiService.getBusStations();
    if (response.isSuccess) {
      myContext.changeTrainStations(response.data);
    }
  }, []);

  useEffect(() => {
    if (!myContext.trainStations || myContext.trainStations.length === 0) fetchStations();
  }, [fetchStations]);

  const onToggleBasePriceEditMode = (id) => {
    setBasePriceRows((state) => {
      return basePricePrevious.map((row) => {
        if (row.id === id) {
          return { ...row, isEditMode: !row.isEditMode };
        }
        return row;
      });
    });
  };

  const onToggleSettingsEditMode = (id) => {
    setAutomaticApprovalSettings((state) => {
      return automaticApprovalSettingsPrevious.map((row) => {
        if (row.id === id) {
          return { ...row, isEditMode: !row.isEditMode };
        }
        return row;
      });
    });
  };

  const onToggleBusNumbersToRemoveAddMode = () => {
    setAutomaticApprovalSettings([
      {
        id: "0",
        Key: AutomaticApprovalSettings.BusNumbersToRemove,
        Title: t("bus_numbers_to_remove"),
        Value: "",
        FromDate: new Date(),
        ToDate: new Date(),
        isAddMode: true,
        DataType: DataType.Int,
      },
      ...automaticApprovalSettingsPrevious,
    ]);
  };

  const onToggleStationsToRemoveAddMode = () => {
    setAutomaticApprovalSettings([
      {
        id: "0",
        Key: AutomaticApprovalSettings.StationsToRemove,
        Title: t("stations_to_remove"),
        Value: "",
        FromDate: new Date(),
        ToDate: new Date(),
        isAddMode: true,
      },
      ...automaticApprovalSettingsPrevious,
    ]);
  };

  const onTogglePeriodsAddMode = () => {
    setAutomaticApprovalSettings([
      {
        id: "0",
        Key: AutomaticApprovalSettings.Periods,
        Title: t("inactive_periods"),
        Value: "false",
        FromDate: new Date(),
        ToDate: new Date(),
        isAddMode: true,
      },
      ...automaticApprovalSettingsPrevious,
    ]);
  };

  const onToggleAddMode = (active) => {
    setIsAddBasePriceMode(active);
    if (active) setBasePriceRows([{ id: "0", Year: "", Value: "", isAddMode: true }, ...basePricePrevious]);
    else setBasePriceRows(basePricePrevious);
  };

  const onSaveBasePrice = async (id) => {
    let item = basePriceRows.find((item) => item.id === id);
    let payload = {
      year: Number(item.Year),
      value: Number(item.Value),
    };
    let response = await BusBasePriceApiService.update(item.Year, payload);
    if (response) {
      await fetchBasePrices();
    } else {
      SnackbarUtils.error(t("error"));
    }
  };

  const onSaveAutomaticApproval = async (id) => {
    let item = automaticApprovalSettings.find((item) => item.id === id);
    await onChangeAutoApprovalSettings(item.Key, item.Value, item.FromDate, item.ToDate);
  };

  const onAddAutomaticApproval = async (id) => {
    let item = automaticApprovalSettings.find((item) => item.id === id);
    await onAddAutoApprovalSettings(item.Key, item.Value, item.FromDate, item.ToDate);
  };

  const onDeleteBasePrice = async (id) => {
    let item = basePriceRows.find((item) => item.id === id);
    await BusBasePriceApiService.deleteByYear(item.Year);
    await fetchBasePrices();
  };

  const onDeleteAutoApprovalSetting = async (id) => {
    let item = automaticApprovalSettings.find((item) => item.id === id);
    await BusAutomaticApprovalApiService.deleteByKey(item.Key);
    await fetchAutomaticApprovalSettings();
  };

  const onAddBasePrice = async (id) => {
    let item = basePriceRows.find((item) => item.id === id);
    if (basePriceRows.find((x) => x.id != item.id && x.Year == item.Year) != null) {
      SnackbarUtils.error(t("base_price_for_year_existed", { year: item.Year }));
    } else {
      if (item.Year.trim() !== "" && item.Value.trim() !== "") {
        let payload = {
          year: Number(item.Year),
          value: Number(item.Value),
        };
        let response = await BusBasePriceApiService.add(payload);
        if (response?.isSuccess) {
          await fetchBasePrices();
        } else {
          SnackbarUtils.error(t("error"));
        }
      }
    }
  };

  useEffect(() => {
    if (basePriceRows === null) fetchBasePrices();
  }, [basePriceRows]);

  useEffect(() => {
    if (automaticApprovalSettings === null) fetchAutomaticApprovalSettings();
  }, [automaticApprovalSettings]);

  const fetchBasePrices = async () => {
    let response = await BusBasePriceApiService.getAll();
    if (response?.isSuccess) {
      let data = response.data
        .map((option, key) => {
          let rObj = {};
          rObj["id"] = option.id;
          rObj["Year"] = option.year;
          rObj["Value"] = option.value;
          rObj["PreviousValue"] = option.previousValue;
          rObj["LastModified"] = option.lastModified;
          return rObj;
        })
        .sort((a, b) => b.Year - a.Year);
      setBasePriceRows(data);
      setBasePricePrevious(data);
      setIsAddBasePriceMode(false);
    }
  };

  const fetchAutomaticApprovalSettings = async () => {
    let response = await BusAutomaticApprovalApiService.getAll();
    if (response?.isSuccess) {
      let autoApproval = response.data.find((item) => item.key === "Enable");
      if (!!autoApproval) setAutoApproval(autoApproval.value?.toLowerCase() === "true" ? true : false);
      let data = response.data.map((item) => {
        let rObj = {};
        rObj["id"] = item.id;
        rObj["Key"] = item.key;
        rObj["Title"] = t(`automatic_approvval_settings.${item.key}`);
        rObj["Value"] = item.value;
        rObj["PreviousValue"] = item.previousValue;
        rObj["FromDate"] = item.fromDate;
        rObj["DataType"] = item.dataType;
        rObj["ToDate"] = item.toDate;
        rObj["LastModified"] = item.lastModified;
        return rObj;
      });
      data = data
        .filter((item) => item.Key !== AutomaticApprovalSettings.Enable)
        .sort(function (a, b) {
          // order by title
          var nameA = a.Title.toUpperCase(); // ignore upper and lowercase
          var nameB = b.Title.toUpperCase(); // ignore upper and lowercase
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          // names must be equal
          return 0;
        });
      setAutomaticApprovalSettings(data);
      setAutomaticApprovalSettingsPrevious(data);
    }
  };

  const onChangeBasePirce = (e, row) => {
    const value = e.target.value;
    if (value < 0) {
      return;
    }
    const name = e.target.name;
    const { id } = row;
    const newRows = basePriceRows.map((row) => {
      if (row.id === id) {
        return { ...row, [name]: value };
      }
      return row;
    });
    setBasePriceRows(newRows);
  };

  const onChangeApprovalSettingRow = (value, name, row) => {
    const { id, Key } = row;
    if (name === "Value" && (row.DataType === DataType.Int || row.DataType === DataType.Decimal)) {
      if (Number(value) < 0) {
        return;
      } else if (row.DataType === DataType.Int && !Number.isInteger(Number(value))) {
        return;
      }
    }
    const newRows = automaticApprovalSettings.map((row) => {
      if (row.id === id) {
        return { ...row, [name]: value };
      }
      return row;
    });
    setAutomaticApprovalSettings(newRows);
  };

  const onChangeAutoApprovalSettings = async (key, value, fromDate, toDate) => {
    if (!value) return;
    if (key.startsWith(AutomaticApprovalSettings.StationsToRemove)) value = value?.code ? value.code : value;
    let payload = { key: key, value: `${value}` };
    if (key.startsWith(AutomaticApprovalSettings.StationsToRemove) || key.startsWith(AutomaticApprovalSettings.BusNumbersToRemove)) {
      if (!!fromDate && !!toDate) {
        let _fromDate = new Date(fromDate);
        let _toDate = new Date(toDate);
        _fromDate.setHours(0, 0, 0, 0);
        _toDate.setHours(0, 0, 0, 0);
        if (_fromDate.getTime() > _toDate.getTime()) return;
        else {
          payload.fromDate = formatDate(fromDate);
          payload.toDate = formatDate(toDate);
        }
      } else return;
    } else if (key.startsWith(AutomaticApprovalSettings.Periods)) {
      if (!!fromDate && !!toDate) {
        let _fromDate = new Date(fromDate);
        let _toDate = new Date(toDate);
        _fromDate.setSeconds(0);
        _toDate.setSeconds(0);
        if (_fromDate.getTime() > _toDate.getTime()) return;
        else {
          payload.fromDate = formatDate(fromDate) + "T" + formatTime(fromDate, true);
          payload.toDate = formatDate(toDate) + "T" + formatTime(toDate, true);
        }
      } else return;
    }
    await BusAutomaticApprovalApiService.update(key, payload);
    await fetchAutomaticApprovalSettings();
  };

  const onAddAutoApprovalSettings = async (row) => {
    let { Key, Value, FromDate, ToDate } = row;
    if (!Value) return;
    if (Key.startsWith(AutomaticApprovalSettings.StationsToRemove)) Value = Value?.code ? Value.code : Value;
    let payload = { key: Key, value: `${Value}` };
    if (Key.startsWith(AutomaticApprovalSettings.StationsToRemove) || Key.startsWith(AutomaticApprovalSettings.BusNumbersToRemove)) {
      if (!!FromDate && !!ToDate) {
        let _fromDate = new Date(FromDate);
        let _toDate = new Date(ToDate);
        _fromDate.setHours(0, 0, 0, 0);
        _toDate.setHours(0, 0, 0, 0);
        if (_fromDate.getTime() > _toDate.getTime()) return;
        else {
          payload.fromDate = formatDate(FromDate);
          payload.toDate = formatDate(ToDate);
        }
      } else return;
    } else if (Key.startsWith(AutomaticApprovalSettings.Periods)) {
      if (!!FromDate && !!ToDate) {
        let _fromDate = new Date(FromDate);
        let _toDate = new Date(ToDate);
        _fromDate.setSeconds(0);
        _toDate.setSeconds(0);
        if (_fromDate.getTime() > _toDate.getTime()) return;
        else {
          payload.fromDate = formatDate(FromDate) + "T" + formatTime(FromDate, true);
          payload.toDate = formatDate(ToDate) + "T" + formatTime(ToDate, true);
        }
      } else return;
    }
    await BusAutomaticApprovalApiService.add(payload);
    await fetchAutomaticApprovalSettings();
  };

  const onRevert = (id) => {
    setBasePriceRows(basePricePrevious);
  };

  const onRevertAutomaticApproval = (id) => {
    setAutomaticApprovalSettings(automaticApprovalSettingsPrevious);
  };

  const automaticApprovalSettingRows = () => {
    var result = automaticApprovalSettings?.filter((item) => !item.Key.startsWith(AutomaticApprovalSettings.BusNumbersToRemove) && !item.Key.startsWith(AutomaticApprovalSettings.StationsToRemove) && !item.Key.startsWith(AutomaticApprovalSettings.Periods) && [AutomaticApprovalSettings.MaxClaimsIn24Hours, AutomaticApprovalSettings.MaxClaimsDuring7Days, AutomaticApprovalSettings.MaxClaimsDuring30Days].indexOf(item.Key) <= -1);
    return result ?? [];
  };

  const automaticApprovalSettingFraudNotiRows = () => {
    var result = automaticApprovalSettings?.filter((item) => item.Key === AutomaticApprovalSettings.MaxClaimsIn24Hours || item.Key === AutomaticApprovalSettings.MaxClaimsDuring7Days || item.Key === AutomaticApprovalSettings.MaxClaimsDuring30Days);
    return result ?? [];
  };

  const automaticApprovalSettingPeriodRows = () => {
    var result = automaticApprovalSettings?.filter((item) => item.Key.startsWith(AutomaticApprovalSettings.Periods));
    return result ?? [];
  };

  const automaticApprovalSettingBusNumberRows = () => {
    var result = automaticApprovalSettings?.filter((item) => item.Key.startsWith(AutomaticApprovalSettings.BusNumbersToRemove));
    return result ?? [];
  };

  const automaticApprovalSettingStationRows = () => {
    var result = automaticApprovalSettings?.filter((item) => item.Key.startsWith(AutomaticApprovalSettings.StationsToRemove));
    return result ?? [];
  };

  const isAddPeriodsMode = () => {
    return automaticApprovalSettingPeriodRows()?.some((item) => item.isAddMode);
  };

  const isAddBusNumberMode = () => {
    return automaticApprovalSettingBusNumberRows()?.some((item) => item.isAddMode);
  };

  const isAddStationMode = () => {
    return automaticApprovalSettingStationRows()?.some((item) => item.isAddMode);
  };

  return (
    <MyContext.Consumer>
      {(context) => (
        <React.Fragment>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("base_price")}
            </Typography>
            {!isAddBasePriceMode && !readOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleAddMode(true)} />}
            {isAddBasePriceMode && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleAddMode(false)} />}
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {basePriceColumns.map((column, key) => (
                    <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {basePriceRows?.map((row, key) => {
                  return (
                    <TableRow hover tabIndex={-1} key={key}>
                      {basePriceColumns.map((column) => {
                        if (column.id === "Actions") {
                          return (
                            <TableCell key={100} align="left">
                              {!row.isEditMode && !isAddBasePriceMode && (
                                <>
                                  <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleBasePriceEditMode(row.id)} />
                                  <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteBasePrice(row.id)} />
                                </>
                              )}
                              {row.isEditMode && !isAddBasePriceMode && (
                                <>
                                  <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveBasePrice(row.id)} />
                                  <Undo className="selector margin top-0 bottom-0" onClick={() => onRevert(row.id)} />
                                </>
                              )}
                              {row.isAddMode && <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddBasePrice(row.id)} />}
                            </TableCell>
                          );
                        }
                        if (row.isAddMode || (row.isEditMode && column.editable)) {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <MyNumberField name={column.id} size="small" value={row[column.id]} onChange={(e) => onChangeBasePirce(e, row)} />
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Tooltip title={column.id === "Value" && row["PreviousValue"] && `${formatDateTime(row["LastModified"])} - ${row["PreviousValue"]}`}>
                              <span>{row[column.id]}</span>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("auto_claim_processing")}
            </Typography>
            <Switch disabled={readOnly} className="margin left-1 top-0 bottom-0" color="primary" checked={autoApproval} onChange={(e, checked) => onChangeAutoApprovalSettings(AutomaticApprovalSettings.Enable, checked.toString())} />
          </Toolbar>
          <TableContainer>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {autoApprovalColumns.map((column, key) => (
                    <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettingRows().map((row, key) => {
                  return (
                    <TableRow hover tabIndex={-1} key={key}>
                      {autoApprovalColumns.map((column) => {
                        if (column.id === "Actions") {
                          return (
                            <TableCell key={100} align="left">
                              {!row.isEditMode && <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />}
                              {row.isEditMode && (
                                <>
                                  <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                  <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                </>
                              )}
                            </TableCell>
                          );
                        }
                        let value = row[column.id];
                        if ((row.isEditMode || row.isAddMode) && column.editable) {
                          if (row.DataType === DataType.Bool) {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <MyTextField size="small" value={value} onChange={(value) => onChangeApprovalSettingRow(value, column.id, row)} />
                              </TableCell>
                            );
                          }
                          if (row.Key == AutomaticApprovalSettings.AllowedFileExtension) {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <MySelect size="small" style={{ width: "15rem" }} label={t("status")} onChange={(values) => onChangeApprovalSettingRow(values.map((item) => item.value).toString(), column.id, row)} options={AllowedFileExtensions} value={value.split(",").map((item) => ({ key: item, value: item }))} />
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <MyNumberField decimalSeparator={row.DataType === DataType.Decimal ? "," : false} size="small" value={value.replace(".", ",")} onChange={(e) => onChangeApprovalSettingRow(e.target.value.replace(",", "."), column.id, row)} />
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Tooltip title={column.id === "Value" && row["PreviousValue"] && `${formatDateTime(row["LastModified"])} - ${row["PreviousValue"]}`}>
                              <span>{value?.replace(".", ",")}</span>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("buses_to_remove")}
            </Typography>
            {!isAddBusNumberMode() && !readOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleBusNumbersToRemoveAddMode()} />}
            {isAddBusNumberMode() && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onRevertAutomaticApproval()} />}
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {busesToRemoveColumns.map((column, key) => (
                    <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettingBusNumberRows().map((row, key) => {
                  return (
                    <TableRow hover tabIndex={-1} key={key}>
                      {busesToRemoveColumns.map((column) => {
                        if (column.id === "Actions") {
                          return (
                            <TableCell key={100} align="left">
                              {!row.isEditMode && !isAddBusNumberMode() && (
                                <>
                                  <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />
                                  <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteAutoApprovalSetting(row.id)} />
                                </>
                              )}
                              {row.isEditMode && !isAddBusNumberMode() && (
                                <>
                                  <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                  <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                </>
                              )}
                              {row.isAddMode && <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddAutoApprovalSettings(row)} />}
                            </TableCell>
                          );
                        }
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {(row.isEditMode || row.isAddMode) && column.editable ? (
                              <>
                                {column.type === "number" && <MyNumberField size="small" value={value} onChange={(e) => onChangeApprovalSettingRow(e.target.value, column.id, row)} />}
                                {column.type === "date" && <MyDatePicker value={value} onAccept={(date) => onChangeApprovalSettingRow(date.toString(), column.id, row)} />}
                              </>
                            ) : column.format ? (
                              column.format(value)
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("stations_to_remove")}
            </Typography>
            {!isAddStationMode() && !readOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onToggleStationsToRemoveAddMode()} />}
            {isAddStationMode() && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onRevertAutomaticApproval()} />}
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {stationsToRemoveColumns.map((column, key) => (
                    <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettingStationRows().map((row, key) => {
                  return (
                    <TableRow hover tabIndex={-1} key={key}>
                      {stationsToRemoveColumns.map((column) => {
                        if (column.id === "Actions") {
                          return (
                            <TableCell key={100} align="left">
                              {!row.isEditMode && !isAddStationMode() && (
                                <>
                                  <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />
                                  <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteAutoApprovalSetting(row.id)} />
                                </>
                              )}
                              {row.isEditMode && !isAddStationMode() && (
                                <>
                                  <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                  <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                </>
                              )}
                              {row.isAddMode && <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddAutoApprovalSettings(row)} />}
                            </TableCell>
                          );
                        }
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {(row.isEditMode || row.isAddMode) && column.editable ? (
                              <>
                                {column.type !== "date" && <MyDropdown onChange={(e, value) => onChangeApprovalSettingRow(value, column.id, row)} value={value?.code ? value : getTrainStationOptions(context.trainStations).find((option) => option.code === value)} options={getTrainStationOptions(context.trainStations)} />}
                                {column.type === "date" && <MyDatePicker value={value} onAccept={(date) => onChangeApprovalSettingRow(date.toString(), column.id, row)} />}
                              </>
                            ) : column.format ? (
                              column.format(value)
                            ) : (
                              value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("inactive_periods")}
            </Typography>
            {!isAddPeriodsMode() && !readOnly && <Add className="selector margin left-1 top-0 bottom-0" onClick={() => onTogglePeriodsAddMode()} />}
            {isAddPeriodsMode() && <Cancel className="selector margin left-1 top-0 bottom-0" onClick={() => onRevertAutomaticApproval()} />}
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }}>
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {periodsColumns.map((column, key) => (
                    <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettingPeriodRows().map((row, key) => {
                  return (
                    <TableRow hover tabIndex={-1} key={key}>
                      {periodsColumns.map((column) => {
                        if (column.id === "Actions") {
                          return (
                            <TableCell key={100} align="left">
                              {!row.isEditMode && !isAddPeriodsMode() && (
                                <>
                                  <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />
                                  <Delete className="selector margin top-0 bottom-0" onClick={() => onDeleteAutoApprovalSetting(row.id)} />
                                </>
                              )}
                              {row.isEditMode && !isAddPeriodsMode() && (
                                <>
                                  <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                  <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                </>
                              )}
                              {row.isAddMode && <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddAutoApprovalSettings(row)} />}
                            </TableCell>
                          );
                        }
                        const value = row[column.id];
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {(row.isEditMode || row.isAddMode) && column.editable ? <>{column.type === "date" && <MyDateTimePicker value={value} onAccept={(date) => onChangeApprovalSettingRow(date.toString(), column.id, row)} />}</> : column.format ? column.format(value) : value}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Toolbar>
            <Typography variant="h6" id="tableTitle" component="div">
              {t("fraud_notification")}
            </Typography>
          </Toolbar>
          <TableContainer sx={{ maxHeight: 440 }} className="margin bottom-7">
            <Table stickyHeader aria-label="sticky table" size="small">
              <TableHead>
                <TableRow>
                  {autoApprovalColumns.map((column, key) => (
                    <TableCell key={key} align={column.align} style={{ width: column.width, minWidth: column.minWidth }}>
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {automaticApprovalSettingFraudNotiRows().map((row, key) => {
                  return (
                    <TableRow hover tabIndex={-1} key={key}>
                      {autoApprovalColumns.map((column) => {
                        if (column.id === "Actions") {
                          return (
                            <TableCell key={100} align="left">
                              {!row.isEditMode && !row.isAddMode && <Edit className="selector margin right-1 top-0 bottom-0" onClick={() => onToggleSettingsEditMode(row.id)} />}
                              {row.isEditMode && (
                                <>
                                  <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onSaveAutomaticApproval(row.id)} />
                                  <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                </>
                              )}
                              {row.isAddMode && (
                                <>
                                  <Done className="selector margin top-0 bottom-0 right-1" onClick={() => onAddAutomaticApproval(row.id)} />
                                  <Undo className="selector margin top-0 bottom-0" onClick={() => onRevertAutomaticApproval(row.id)} />
                                </>
                              )}
                            </TableCell>
                          );
                        }
                        let value = row[column.id];
                        if ((row.isEditMode || row.isAddMode) && column.editable) {
                          return (
                            <TableCell key={column.id} align={column.align}>
                              <MyNumberField size="small" value={value} onChange={(e) => onChangeApprovalSettingRow(e.target.value, column.id, row)} />
                            </TableCell>
                          );
                        }
                        return (
                          <TableCell key={column.id} align={column.align}>
                            <Tooltip title={column.id === "Value" && row["PreviousValue"] && `${formatDateTime(row["LastModified"])} - ${row["PreviousValue"]}`}>
                              <span>{value}</span>
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </React.Fragment>
      )}
    </MyContext.Consumer>
  );
};
